import { useEffect, useState, useCallback } from "react";
import { http, formatApiError, formatINR, formatPct } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import TickerSearch from "../components/TickerSearch";
import { toast } from "sonner";
import { Plus, Trash, Sparkle, Star, TrendUp, TrendDown } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const EXCHANGES = ["NSE", "BSE", "US", "CRYPTO", "OTHER"];
const EMPTY = { symbol: "", name: "", exchange: "NSE", target_price: "", notes: "" };

const VERDICT_META = {
  STRONG_FIT: { color: "text-emerald-300", bg: "bg-emerald-500/15 border-emerald-700/40", label: "Strong fit" },
  GOOD_FIT:   { color: "text-teal-300",    bg: "bg-teal-500/15 border-teal-700/40",       label: "Good fit" },
  NEUTRAL:    { color: "text-zinc-300",    bg: "bg-zinc-500/15 border-zinc-700/40",       label: "Neutral" },
  WEAK_FIT:   { color: "text-amber-300",   bg: "bg-amber-500/15 border-amber-700/40",     label: "Weak fit" },
  AVOID:      { color: "text-red-300",     bg: "bg-red-500/15 border-red-700/40",         label: "Avoid" },
};

function scoreColor(score) {
  if (score >= 80) return "text-emerald-400";
  if (score >= 60) return "text-teal-400";
  if (score >= 40) return "text-amber-400";
  return "text-red-400";
}

export default function Watchlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/watchlist");
      setItems(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.symbol) { toast.error("Pick a symbol"); return; }
    try {
      await http.post("/watchlist", {
        symbol: form.symbol.toUpperCase(),
        name: form.name || null,
        exchange: form.exchange,
        target_price: form.target_price ? parseFloat(form.target_price) : null,
        notes: form.notes || null,
      });
      toast.success(`${form.symbol} added to watchlist`);
      setOpen(false); setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id, symbol) => {
    try {
      await http.delete(`/watchlist/${id}`);
      toast.success(`${symbol} removed`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const grade = async () => {
    if (items.length === 0) { toast.error("Add tickers to grade first"); return; }
    setGrading(true);
    try {
      const { data } = await http.post("/watchlist/grade");
      setSummary({ text: data.summary, provider: data.provider, at: data.generated_at });
      toast.success(`Graded ${data.grades?.length || 0} tickers`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGrading(false); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-bold">Watchlist</div>
          <h1 className="mt-2 font-display text-4xl font-light">Tickers to watch</h1>
          <p className="text-zinc-400 mt-2 text-sm">Track prices for what you don't yet own. Let the AI score each one against your investing style.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={grade} disabled={grading || items.length === 0} className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="grade-watchlist-button">
            <Sparkle size={16} weight="fill" className="mr-2" />
            {grading ? "Grading…" : "Grade with AI"}
          </Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-zinc-900 border-zinc-800" data-testid="add-watch-button">
                <Plus size={16} className="mr-2" /> Add ticker
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <DialogHeader><DialogTitle>Add to watchlist</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Symbol</Label>
                  <div className="mt-1">
                    <TickerSearch
                      value={form.symbol}
                      onChange={(v) => setForm((f) => ({ ...f, symbol: v }))}
                      onSelect={(r) => setForm((f) => ({
                        ...f, symbol: r.symbol,
                        name: r.name || f.name,
                        exchange: r.exchange || f.exchange,
                      }))}
                      testId="watch-symbol-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Exchange</Label>
                  <Select value={form.exchange} onValueChange={(v) => setForm({ ...form, exchange: v })}>
                    <SelectTrigger data-testid="watch-exchange-select" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      {EXCHANGES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target price (₹) — optional</Label>
                  <Input data-testid="watch-target-input" type="number" step="any" value={form.target_price}
                    onChange={(e) => setForm({ ...form, target_price: e.target.value })}
                    placeholder="Entry / alert price"
                    className="mt-1 bg-zinc-900 border-zinc-800" />
                </div>
                <div className="col-span-2">
                  <Label>Notes (optional)</Label>
                  <Input data-testid="watch-notes-input" value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Why is this on your radar?"
                    className="mt-1 bg-zinc-900 border-zinc-800" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submit} data-testid="watch-save-button">Add</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {summary && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 mb-6" data-testid="watchlist-summary">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
            <Sparkle size={12} weight="fill" className="text-emerald-400" />
            AI overview
          </div>
          <p className="mt-2 text-zinc-200">{summary.text}</p>
          <div className="mt-3 text-xs text-zinc-500 font-mono">
            {summary.provider === "aws_bedrock" ? "AWS Bedrock" : "AI"} · {new Date(summary.at).toLocaleString("en-IN")}
          </div>
        </motion.div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-zinc-900 border border-dashed border-zinc-800 rounded-lg p-16 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-400">
            <Star size={26} weight="fill" />
          </div>
          <div className="mt-6 font-display text-xl">Your watchlist is empty</div>
          <p className="mt-2 text-sm text-zinc-500 max-w-md mx-auto">
            Add tickers you're considering, set a target entry price, and let the AI grade them against your style.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((w, i) => {
            const grade = w.grade || {};
            const meta = VERDICT_META[grade.verdict] || null;
            const dayUp = w.day_change_pct >= 0;
            return (
              <motion.div key={w.id}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className="card-hover bg-zinc-900 border border-zinc-800 rounded-lg p-5"
                data-testid={`watch-row-${w.symbol}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-lg">{w.symbol}</div>
                      <span className="text-[10px] font-mono uppercase text-zinc-500 border border-zinc-800 rounded px-1.5 py-0.5">
                        {w.exchange}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">{w.name || "—"}</div>
                  </div>
                  <Button size="icon" variant="ghost" className="text-red-400 hover:text-red-300"
                    onClick={() => del(w.id, w.symbol)} data-testid={`delete-watch-${w.symbol}`}>
                    <Trash size={16} />
                  </Button>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Price</div>
                    <div className="mt-1 font-mono">
                      {w.current_price ? formatINR(w.current_price) : <span className="text-zinc-600">—</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Day</div>
                    <div className={`mt-1 font-mono text-sm flex items-center gap-1 ${dayUp ? "text-emerald-400" : "text-red-400"}`}>
                      {w.day_change_pct != null ? (
                        <>
                          {dayUp ? <TrendUp size={12} /> : <TrendDown size={12} />}
                          {formatPct(w.day_change_pct)}
                        </>
                      ) : <span className="text-zinc-600">—</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">To target</div>
                    <div className="mt-1 font-mono text-sm">
                      {w.distance_to_target_pct != null
                        ? <span className={w.distance_to_target_pct > 0 ? "text-amber-400" : "text-emerald-400"}>
                            {formatPct(w.distance_to_target_pct)}
                          </span>
                        : <span className="text-zinc-600">—</span>}
                    </div>
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-zinc-800">
                  {!meta ? (
                    <div className="text-xs text-zinc-500">
                      <Sparkle size={12} weight="fill" className="inline mr-1 text-zinc-600" />
                      Not graded yet — click <span className="text-emerald-400">Grade with AI</span> above.
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${meta.bg} ${meta.color} text-xs font-medium`}
                          data-testid={`watch-verdict-${w.symbol}`}>
                          {meta.label}
                        </span>
                        {grade.score != null && (
                          <div className={`font-mono text-lg ${scoreColor(grade.score)}`} data-testid={`watch-score-${w.symbol}`}>
                            {grade.score}<span className="text-xs text-zinc-500">/100</span>
                          </div>
                        )}
                      </div>
                      {grade.reason && <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{grade.reason}</p>}
                      {grade.suggested_entry_price != null && (
                        <div className="mt-2 text-xs text-zinc-500">
                          Suggested entry: <span className="text-emerald-400 font-mono">{formatINR(grade.suggested_entry_price)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
